import { useCallback, useEffect, useRef, useState } from "react";
import * as apiHelper from "../../../Services/ApiHelper";
import * as toast from "../../../Wrapper/toast/toast";
import { changeDateFormatForServer } from "../../../Utils/dateUtils";
import { useNavigate } from "react-router-dom";
import { getLocalData } from "../../../Services/common";
import { CONFIG } from "../../../Constants/storageConstant";
import TrackPatientUI from "./TrackPatientUI";
import DownloadPatientsModal from "./DownloadPatientsModal";

const TrackPatient = () => {
  const navigate = useNavigate();
  const STORAGE_KEY = "track_patient_filters";

  // Initialize filters from localStorage if available, otherwise use defaults
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem(STORAGE_KEY);
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        // Ensure dates are null if they were null (JSON.stringify converts null correctly, but just being safe)
        return parsed;
      } catch (e) {
        console.error("Error parsing saved filters", e);
      }
    }
    return {
      startRange: { startDate: null, endDate: null },
      endRange: { startDate: null, endDate: null },
      activeStatus: "active",
      selectedMedicines: [],
      selectedServices: [],
      selectedGender: [],
      selectedCity: [],
      selectedDiagnosis: [],
      selectedDoctor: [],
      ageRange: { from: "", to: "" },
    };
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const [patientsList, setPatientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [allDiagnosis, setAllDiagnosis] = useState([]);

  const onPageChange = (page) => {
    getPatientsList(page);
  };

  // Builds the exact same filter payload `getPatientsList` sends, minus
  // pagination — so the BE estimate / export endpoints see the same set of
  // rows the user is currently looking at, just without `LIMIT 10`.
  //
  // Memoized so the DownloadPatientsModal's useEffect (deps include this
  // callback) only re-fires the /estimate request when filters actually
  // change — not on every parent re-render.
  const buildFilterPayload = useCallback(() => {
    const {
      activeStatus,
      selectedGender,
      selectedCity,
      ageRange,
      selectedDiagnosis,
      selectedDoctor,
      selectedMedicines,
      selectedServices,
      startRange,
      endRange,
    } = filters;

    return {
      activeStatus,
      gender: selectedGender,
      city: selectedCity,
      age_from: ageRange.from,
      age_to: ageRange.to,
      diagnosis: selectedDiagnosis,
      doctor: selectedDoctor,
      medicines: selectedMedicines,
      services: selectedServices,
      start_from_date: changeDateFormatForServer(startRange.startDate),
      start_to_date: changeDateFormatForServer(startRange.endDate),
      end_from_date: changeDateFormatForServer(endRange.startDate),
      end_to_date: changeDateFormatForServer(endRange.endDate),
    };
  }, [filters]);

  const getPatientsList = async (page = 1) => {
    setLoading(true);

    const queryFilters = {
      ...buildFilterPayload(),
      page,
      limit: 10,
    };

    try {
      apiHelper.getPatientsTrackReport(queryFilters).then((response) => {
        setLoading(false);
        if (response?.isSuccess) {
          setPatientsList(response?.data);
        } else {
          toast.warn(response?.message);
        }
      });
    } catch (err) {
      toast.error("Something went wrong");
      setLoading(false);
    }
  };

  // The actual download is handled inside DownloadPatientsModal — the
  // button just opens the dialog. Two-step flow exists because:
  //   1) we need to show count + estimated time before committing, and
  //   2) the user picks PDF or CSV from inside the dialog.
  const openDownloadModal = () => setShowDownloadModal(true);
  const closeDownloadModal = () => setShowDownloadModal(false);

  const getDiagnosisList = (page = 1) => {
    const query = `?page=${page}&limit=1000`;

    apiHelper
      .getDiagnosisList(query)
      .then((response) => {
        if (response?.isSuccess) {
          setAllDiagnosis(response?.data?.data);
        } else {
          toast.warn(response?.message);
        }
      })
      .catch((err) => toast.warn(err))
      .finally(() => setLoading(false));
  };

  const openDetailsPage = (id) => navigate(`/patient-visit/${id}`);

  useEffect(() => {
    getDiagnosisList();
  }, []);

  const resetFilters = () => {
    setFilters({
      startRange: { startDate: null, endDate: null },
      endRange: { startDate: null, endDate: null },
      activeStatus: "active",
      selectedMedicines: [],
      selectedServices: [],
      selectedGender: [],
      selectedCity: [],
      selectedDiagnosis: [],
      selectedDoctor: [],
      ageRange: { from: "", to: "" },
    });
  };

  return (
    <>
      <TrackPatientUI
        filters={filters}
        setFilters={setFilters}
        openDetailsPage={openDetailsPage}
        patientsList={patientsList}
        loading={loading}
        getPatientsList={getPatientsList}
        allDiagnosis={allDiagnosis}
        onPageChange={onPageChange}
        resetFilters={resetFilters}
        onDownloadClick={openDownloadModal}
      />
      <DownloadPatientsModal
        show={showDownloadModal}
        onClose={closeDownloadModal}
        buildFilterPayload={buildFilterPayload}
      />
    </>
  );
};

export default TrackPatient;
